import React from "react"
import { Link } from "gatsby"
import Layout from "../components/Layout"
import ImageGallery from "../components/ImageGallery"

const siteUrl = "https://gregwoodwardphoto.com"

const SERVICE_LABELS = {
  commercial: "Commercial photography",
  lifestyle: "Lifestyle photography",
  sports: "Sports photography",
  "moving-image": "Moving image",
}

export default function Gallery({ pageContext }) {
  const { node } = pageContext
  const pathname = `/gallery/${node.slug}/`
  const description =
    node.description ||
    node.summary ||
    `${node.title} photography and moving-image work by Greg Woodward.`

  const images = (node.content || [])
    .filter(item => item.fixed)
    .map(item => `https:${item.fixed.src}`)

  const videos = (node.content || [])
    .filter(item => !item.fixed && item.file?.url)
    .map(item => `https:${item.file.url}`)

  const gallerySchema = {
    "@type": "ImageGallery",
    name: node.title,
    description,
    url: `${siteUrl}${pathname}`,
    creator: { "@type": "Person", name: "Greg Woodward", url: siteUrl },
    ...(node.client && { about: node.client }),
    ...(images.length && { image: images }),
  }

  const schemaGraph = [
    gallerySchema,
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Work",
          item: `${siteUrl}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: node.title,
          item: `${siteUrl}${pathname}`,
        },
      ],
    },
  ]

  videos.forEach((videoUrl, i) => {
    schemaGraph.push({
      "@type": "VideoObject",
      name: `${node.title}${videos.length > 1 ? `, clip ${i + 1}` : ""}`,
      description: description,
      contentUrl: videoUrl,
      ...(images[0] && { thumbnailUrl: images[0] }),
      uploadDate: node.updatedAt || new Date().toISOString().split("T")[0],
    })
  })

  const schema = {
    "@context": "https://schema.org",
    "@graph": schemaGraph,
  }

  const relatedServices = (node.relatedServices || []).filter(
    slug => SERVICE_LABELS[slug]
  )

  return (
    <Layout
      seo={{
        title: `${node.title} | Greg Woodward Photography`,
        description,
        pathname,
        image: images[0],
        schema,
      }}
    >
      <main className="case-study">
        <div className="page-intro">
          <div>
            {node.client && <span className="service-label">{node.client}</span>}
            <h1>{node.title}</h1>
          </div>
          {(node.summary || node.description) && (
            <p>{node.summary || node.description}</p>
          )}
        </div>

        {(node.brief || node.approach) && (
          <div className="case-study__grid">
            {node.brief && (
              <section>
                <span className="service-label">The brief</span>
                <p>{node.brief}</p>
              </section>
            )}
            {node.approach && (
              <section>
                <span className="service-label">The approach</span>
                <p>{node.approach}</p>
              </section>
            )}
          </div>
        )}

        {node.experience && (
          <div className="case-study__prose">
            <span className="service-label">The experience</span>
            <p>{node.experience}</p>
          </div>
        )}

        {node.content?.length > 0 && (
          <div className="case-study__work">
            <span className="service-label">The work</span>
            <ImageGallery images={node.content} galleryTitle={node.title} />
          </div>
        )}

        {(node.deliverables || node.usage) && (
          <div className="case-study__grid">
            {node.deliverables && (
              <section>
                <span className="service-label">Deliverables</span>
                <p>{node.deliverables}</p>
              </section>
            )}
            {node.usage && (
              <section>
                <span className="service-label">Usage</span>
                <p>{node.usage}</p>
              </section>
            )}
          </div>
        )}

        {node.outcome && (
          <div className="case-study__prose">
            <span className="service-label">Outcome</span>
            <p>{node.outcome}</p>
          </div>
        )}

        {relatedServices.length > 0 && (
          <div className="case-study__related">
            <span className="service-label">Related services</span>
            <div className="case-study__related-links">
              {relatedServices.map(slug => (
                <Link key={slug} to={`/services/${slug}/`}>
                  {SERVICE_LABELS[slug]} <span aria-hidden="true">↗</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="service-cta">
          <span>Have a similar project in mind?</span>
          <Link to="/contact/">
            Discuss a project <span aria-hidden="true">↗</span>
          </Link>
        </div>

        <Link className="case-study__back" to="/">
          ← Back to work
        </Link>
      </main>
    </Layout>
  )
}
